<script lang="ts" setup>
    import { onMounted, ref, reactive } from 'vue';
    import { IToolbar, ToolbarStyle, ToolbarBackground, ToolbarPadding } from "../lib/Toolbar";
    import { DocumentLocationTypes } from '../lib/Api/DocumentManager';
    import { ModalStyles, popAlert } from "../lib/Notification";
    import { ActionButtonStyle } from "../lib/Types/Types";
    import Toolbar from "../components/Toolbar.vue";
    import TextInput from '../components/TextInput.vue';
    import * as Validation from "../lib/Validation";
    import * as ChoiceApi from "../lib/ChoiceApi";

    const props = withDefaults(defineProps<{
        patientId: number,
        folderId: number,
        filename?: string,
        startVideo?: boolean
    }>(), {
        filename: "Headshot",
        startVideo: false
    });

    defineExpose({openVideo,closeVideo});
    const emit = defineEmits(["saveHeadshot"]);

    const photoCanvas = ref<HTMLCanvasElement|null|undefined>(null);

    const formData = reactive<{
        imageCapture?: ImageCapture,
        track?: MediaStreamTrack,
        filename?: string
    }>({
    });

    enum photoToolbar {
        Capture,
        Save
    }

    const photoCaptureConfig = reactive<IToolbar>({
        toolbarList: [
            {identifier: photoToolbar.Capture, label: "Capture", tooltip:"Capture the photo.", action: ActionButtonStyle.Camera, enabled: false, performAction: () => onCapturePhoto()},
            {identifier: photoToolbar.Save, label: "Save", tooltip:"Save the captured photo.", action: ActionButtonStyle.Save, enabled: false, performAction: () => saveCapturePhoto()},
        ]
    });

    onMounted(async () => {
        formData.filename = props.filename;
        if (props.startVideo)
            openVideo();
    });

    function openVideo() {
        // Clear the canvas
        if (photoCanvas.value)
            photoCanvas.value.getContext('2d')?.clearRect(0, 0, photoCanvas.value.width, photoCanvas.value.height);
        formData.filename = props.filename;

        navigator.mediaDevices.getUserMedia({video: true})
            .then(mediaStream => {
                const element = document.querySelector('video')!;
                element.srcObject = mediaStream;

                formData.track = mediaStream.getVideoTracks()[0];
                formData.imageCapture = new ImageCapture(formData.track);

                photoCaptureConfig.toolbarList[photoToolbar.Capture].enabled = true;
                photoCaptureConfig.toolbarList[photoToolbar.Save].enabled = false;
            })
            .catch(error => 
                openVideoError(error)
            ); 
    }

    function openVideoError(error: any) {
        console.log(error);
        popAlert(`Camera Not Detected -  Check camera, privacy, and permissions for the device and try again.`, "Take Photo", ModalStyles.warning);
    }

    function closeVideo() {
        photoCaptureConfig.toolbarList[photoToolbar.Capture].enabled = false;
        photoCaptureConfig.toolbarList[photoToolbar.Save].enabled = false;
        if (formData.track)
            formData.track.stop();        
    }

    async function onCapturePhoto() {
        formData.imageCapture.takePhoto()
            .then((blob: ImageBitmapSource) => createImageBitmap(blob))
            .then((img: ImageBitmap) => {
                displayOnCanvas(img);
            })
            .catch((error: any) => console.log(error));
    }

    function displayOnCanvas(img: ImageBitmap) {
        if (!photoCanvas.value) {
            popAlert(`Failed to capture the photo.`, "Capture Photo", ModalStyles.warning);
            return;
        }
        photoCanvas.value.width = parseInt(getComputedStyle(photoCanvas.value).width.split('px')[0], 10);
        photoCanvas.value.height = parseInt(getComputedStyle(photoCanvas.value).height.split('px')[0], 10);
        let ratio = photoCanvas.value.height / img.height;
        let x = (photoCanvas.value.width - img.width * ratio) / 2;
        let y = (photoCanvas.value.height - img.height * ratio) / 2;
        photoCanvas.value.getContext('2d')?.clearRect(0, 0, photoCanvas.value.width, photoCanvas.value.height);
        photoCanvas.value.getContext('2d')?.drawImage(img, 0, 0, img.width, img.height, x, y, img.width * ratio, img.height * ratio);
        photoCaptureConfig.toolbarList[photoToolbar.Save].enabled = true;
    }

    async function saveCapturePhoto() {
        if (!photoCanvas.value) {
            popAlert(`Failed to save the captured photo.`, "Save", ModalStyles.warning);
            return;
        }
        const payload = {
            locationType: DocumentLocationTypes.Patient,
            locationId: props.patientId,
            documentName: `${formData.filename ?? "Headshot"}.jpg`,
            base64Data: photoCanvas.value.toDataURL("image/jpeg"),
            folderId: props.folderId
        }
        const result = await ChoiceApi.documentManager.saveDocument(payload);
        if (result.success) {
            emit("saveHeadshot");
            MessageLib.Edocument.refreshHeadshot({patientId: props.patientId, documentId: result.documentId});
        }
        else {
            popAlert(`Failed to save the patient photograph.`, "Save", ModalStyles.warning);
        }
    }

</script>

<style scoped>
    .photoCanvasSize {
        width: 200px;
        height: 260px;
        border: 1px solid gray;
    }
    .photoColumn {
        width: 220px;
    }
    .videoSize {
        width: 540px;
    }
</style>

<template>
    <div class="row">
        <div class="col">
            <video class="videoSize" autoplay></video>
        </div>
        <div class="photoColumn">
            <div class="row">
                <div class="col">
                    <canvas ref="photoCanvas" class="photoCanvasSize"></canvas>
                </div>
                <div class="w-100"></div>
                <div class="col">
                    <TextInput 
                        v-model:text-value="formData.filename" 
                        :maxLength="96"
                        placeholder="Headshot name"
                        :acceptCharacter="Validation.LimitToFilenameCharacters"
                        :cssFramework = "'vanilla'">
                        <template #RightAddon>
                            .jpg
                        </template>
                    </TextInput>
                </div>
                <div class="w-100"></div>
                <div class="col mt-2">
                    <Toolbar 
                        :configuration="photoCaptureConfig" 
                        :toolbarStyle="ToolbarStyle.outline" 
                        :toolbarPadding="ToolbarPadding.paddingOnlyLeftRight"
                        :toolbarBackground="ToolbarBackground.white"/>                 
                </div>
            </div>
        </div>
    </div>
</template>